import React, { useState, useEffect, useCallback } from 'react';
import { Cloud } from 'lucide-react';
import TransportSelect from './TransportSelect';
import BindemittelSelect from './BindemittelSelect';
import StorageSelect from './StorageSelect';
import FuelSelect from './FuelSelect';
import ProductionTypeSelect from './ProductionTypeSelect';
import { simulationService } from '../../services/simulationService';

const Calculator = ({ selectedMaterial, projectId, onSave, onPermanentSave, onMaterialSpecificationTransfer }) => {
  const [materialName, setMaterialName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [transportValues, setTransportValues] = useState({
    fueller: 'Sattelzug Diesel',
    asphalt: 'Sattelzug Diesel',
    bindemittel: 'Sattelzug Diesel'
  });

  const [selectedBindemittel, setSelectedBindemittel] = useState('');
  const [selectedProductionType, setSelectedProductionType] = useState('');
  const [storageValues, setStorageValues] = useState({
    gestein: '',
    asphalt: ''
  });

  const [fuelValues, setFuelValues] = useState({
    gestein: '',
    asphalt: ''
  });

  const [materialPercentages, setMaterialPercentages] = useState({
    fueller: '',
    asphalt: '',
    bindemittel: ''
  });

  const [distances, setDistances] = useState({
    fueller: '',
    asphalt: '',
    bindemittel: ''
  });

  // Individual gestein components (replaces single gestein field)
  const [gesteinComponents, setGesteinComponents] = useState([
    { nr: '', bezeichnung: 'Gesteinskörnungen', percentage: '', distance: '', transport: 'Sattelzug Diesel' }
  ]);

  const [constructionSiteTransport, setConstructionSiteTransport] = useState({
    distance: '',
    vehicle: 'Sattelzug Diesel'
  });

  const [co2eValue, setCo2eValue] = useState(null);
  const [co2eBreakdown, setCo2eBreakdown] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState(null);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  // Helper function to get field styling based on validation state
  const getFieldStyling = (value, isRequired = false) => {
    const baseClasses = "w-full p-3 rounded-xl text-gray-600 focus:outline-none transition-all";

    if (isRequired && (!value || value === '')) {
      // Required field that's empty - orange border
      return `${baseClasses} bg-orange-50 border-2 border-orange-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500`;
    } else if (value && value !== '') {
      // Field has value - green border
      return `${baseClasses} bg-green-50 border-2 border-green-200 focus:ring-2 focus:ring-green-500 focus:border-green-500`;
    } else {
      // Optional field or default state - gray border
      return `${baseClasses} bg-gray-50 border border-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent`;
    }
  };

  // Helper function to get select field styling
  const getSelectStyling = (value, isRequired = false) => {
    const baseClasses = "w-full p-3 rounded-xl appearance-none cursor-pointer text-gray-600 focus:outline-none transition-all";

    if (isRequired && (!value || value === '')) {
      // Required field that's empty - orange border
      return `${baseClasses} bg-orange-50 border-2 border-orange-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500`;
    } else if (value && value !== '') {
      // Field has value - green border
      return `${baseClasses} bg-green-50 border-2 border-green-200 focus:ring-2 focus:ring-green-500 focus:border-green-500`;
    } else {
      // Optional field or default state - gray border
      return `${baseClasses} bg-gray-50 border border-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent`;
    }
  };

  // Validation function to check all required fields
  const validateRequiredFields = () => {
    const missing = [];

    // Check material percentages
    if (!materialPercentages.fueller || materialPercentages.fueller === '') {
      missing.push('Füller M.-%');
    }
    // Check gestein components
    gesteinComponents.forEach((comp, index) => {
      const label = comp.bezeichnung || `Gesteinskörnungen ${index + 1}`;
      if (!comp.percentage || comp.percentage === '') {
        missing.push(`${label} M.-%`);
      }
      if (!comp.distance || comp.distance === '') {
        missing.push(`${label} Entfernung (km)`);
      }
    });
    if (!materialPercentages.asphalt || materialPercentages.asphalt === '') {
      missing.push('Asphaltgranulat M.-%');
    }
    if (!materialPercentages.bindemittel || materialPercentages.bindemittel === '') {
      missing.push('Frischbindemittelzugabe M.-%');
    }

    // Check distances
    if (!distances.fueller || distances.fueller === '') {
      missing.push('Füller Entfernung (km)');
    }
    if (!distances.asphalt || distances.asphalt === '') {
      missing.push('Asphaltgranulat Entfernung (km)');
    }
    if (!distances.bindemittel || distances.bindemittel === '') {
      missing.push('Frischbindemittelzugabe Entfernung (km)');
    }

    // Check bindemittel selection
    if (!selectedBindemittel || selectedBindemittel === '') {
      missing.push('Frischbindemittel Typ');
    }

    // Check production type
    if (!selectedProductionType || selectedProductionType === '') {
      missing.push('Herstellungsart');
    }

    // Check storage values
    if (!storageValues.gestein || storageValues.gestein === '') {
      missing.push('Gesteinskörnungen Lagerung');
    }
    if (!storageValues.asphalt || storageValues.asphalt === '') {
      missing.push('Asphaltgranulat Lagerung');
    }

    // Check fuel values
    if (!fuelValues.gestein || fuelValues.gestein === '') {
      missing.push('Gesteinskörnungen Brennstoff');
    }
    if (!fuelValues.asphalt || fuelValues.asphalt === '') {
      missing.push('Asphaltgranulat Brennstoff');
    }

    // Check material name
    if (!materialName || materialName.trim() === '') {
      missing.push('Material Name');
    }

    // Check construction site transport
    if (!constructionSiteTransport.distance || constructionSiteTransport.distance === '') {
      missing.push('Transport Baustelle Entfernung (km)');
    }
    if (!constructionSiteTransport.vehicle || constructionSiteTransport.vehicle === '') {
      missing.push('Transport Baustelle Transportmittel');
    }

    return missing;
  };

  // Reset all form fields to default values
  const resetFormFields = () => {
    // Reset material percentages
    setMaterialPercentages({
      fueller: '',
      asphalt: '',
      bindemittel: ''
    });

    // Reset gestein components
    setGesteinComponents([
      { nr: '', bezeichnung: 'Gesteinskörnungen', percentage: '', distance: '', transport: 'Sattelzug Diesel' }
    ]);

    // Reset distances
    setDistances({
      fueller: '',
      asphalt: '',
      bindemittel: ''
    });

    // Reset transport values
    setTransportValues({
      fueller: 'Sattelzug Diesel',
      asphalt: 'Sattelzug Diesel',
      bindemittel: 'Sattelzug Diesel'
    });

    // Reset bindemittel to empty
    setSelectedBindemittel('');

    // Reset production type
    setSelectedProductionType('');

    // Reset storage values
    setStorageValues({
      gestein: '',
      asphalt: ''
    });

    // Reset fuel values
    setFuelValues({
      gestein: '',
      asphalt: ''
    });

    // Reset construction site transport
    setConstructionSiteTransport({
      distance: '',
      vehicle: 'Sattelzug Diesel'
    });

    // Reset saved data flag
    setHasSavedData(false);
  };

  useEffect(() => {
    if (selectedMaterial?.name) {
      // Reset form fields first
      resetFormFields();

      // Set the material name
      setMaterialName(selectedMaterial.name);
      setDisplayName(selectedMaterial.name);

      // Then try to load last saved calculation if available
      if (projectId && selectedMaterial?.name) {
        // For calculated layers, we don't need location data
        // For delivery notes, we need location data
        const isCalculatedLayer = selectedMaterial.source === 'calculatedLayers';
        const hasLocationData = selectedMaterial.productionPlant?.location;

        if (isCalculatedLayer || hasLocationData) {
          loadLastCalculation();
        }
      }
    }
  }, [selectedMaterial]);

  // Function to load the last saved calculation
  const loadLastCalculation = async () => {
    if (!selectedMaterial?.name || !projectId) {
      return;
    }

    // Check if this is a calculated layer material
    const isCalculatedLayer = selectedMaterial.source === 'calculatedLayers';

    if (!isCalculatedLayer && !selectedMaterial.productionPlant?.location) {
      return;
    }

    setIsLoadingSaved(true);

    try {
      let result;

      if (isCalculatedLayer) {
        // Load from layer areas collection
        const layerTypeMap = {
          'Deckschicht': 'deckschicht',
          'Binderschicht': 'binderschicht',
          'Tragschicht 1': 'tragschicht1',
          'Tragschicht 2': 'tragschicht2'
        };

        const layerType = layerTypeMap[selectedMaterial.layerName];
        if (layerType) {
          result = await simulationService.getLayerCO2(projectId, layerType);
          if (result.success && result.data?.co2Data) {
            result.data.calculationInputs = result.data.co2Data.calculationInputs;
          }
        }
      } else {
        // Load from delivery notes collection (original logic)
        const { postalCode, city } = selectedMaterial.productionPlant.location;
        result = await simulationService.getLastCalculation(
          projectId,
          selectedMaterial.name,
          { postalCode, city }
        );
      }

      if (result.success && result.data) {
        const { calculationInputs } = result.data;

        // Set the calculation inputs
        if (calculationInputs) {
          // Set material percentages
          if (calculationInputs.materialPercentages) {
            setMaterialPercentages(calculationInputs.materialPercentages);
          }

          // Set gestein components
          if (calculationInputs.gesteinComponents && calculationInputs.gesteinComponents.length > 0) {
            setGesteinComponents(calculationInputs.gesteinComponents);
          } else if (calculationInputs.materialPercentages?.gestein !== undefined) {
            // Backward compatibility: convert old single gestein to component
            setGesteinComponents([{
              nr: '',
              bezeichnung: 'Gesteinskörnungen',
              percentage: calculationInputs.materialPercentages.gestein,
              distance: calculationInputs.distances?.gestein || '',
              transport: calculationInputs.transportValues?.gestein || 'Sattelzug Diesel'
            }]);
          }

          // Set distances
          if (calculationInputs.distances) {
            setDistances(calculationInputs.distances);
          }

          // Set transport values
          if (calculationInputs.transportValues) {
            setTransportValues(calculationInputs.transportValues);
          }

          // Set bindemittel
          if (calculationInputs.selectedBindemittel) {
            setSelectedBindemittel(calculationInputs.selectedBindemittel);
          }

          // Set production type
          if (calculationInputs.selectedProductionType) {
            setSelectedProductionType(calculationInputs.selectedProductionType);
          }

          // Set storage values
          if (calculationInputs.storageValues) {
            setStorageValues(calculationInputs.storageValues);
          }

          // Set fuel values
          if (calculationInputs.fuelValues) {
            setFuelValues(calculationInputs.fuelValues);
          }

          // Set construction site transport
          if (calculationInputs.constructionSiteTransport) {
            setConstructionSiteTransport(calculationInputs.constructionSiteTransport);
            console.log('Loaded construction site transport:', calculationInputs.constructionSiteTransport);
          } else {
            console.log('No construction site transport data found in saved inputs');
          }

          console.log('Loaded last saved calculation inputs');
          setHasSavedData(true);
        } else {
          console.log('No calculation inputs found in saved data');
          setHasSavedData(false);
        }
      } else {
        console.log('No saved calculation found for this material');
        setHasSavedData(false);
      }
    } catch (error) {
      console.error('Error loading last calculation:', error);
      // Continue with default values if loading fails
    } finally {
      setIsLoadingSaved(false);
    }
  };

  // Check if we have any actual input values
  const hasInputs = Object.values(materialPercentages).some(val => val !== '' && val !== null) ||
                   Object.values(distances).some(val => val !== '' && val !== null) ||
                   gesteinComponents.some(comp => (comp.percentage !== '' && comp.percentage !== null) || (comp.distance !== '' && comp.distance !== null));

  const calculateCO2e = async () => {
    setIsCalculating(true);
    setCalculationError(null);

    try {
      // If no inputs, set CO2e value to 0
      if (!hasInputs) {
        setCo2eValue(0);
        setCo2eBreakdown(null);
        setIsCalculating(false);
        return;
      }

      const calculationData = {
        materialPercentages,
        distances,
        transportValues,
        gesteinComponents,
        selectedBindemittel,
        selectedProductionType,
        storageValues,
        fuelValues,
        constructionSiteTransport
      };

      const result = await simulationService.calculateCO2e(calculationData);
      setCo2eValue(result.co2e);
      setCo2eBreakdown({
        A1: result.breakdown.A1,
        A2: result.breakdown.A2,
        A3: result.breakdown.A3,
        A4: result.breakdown.A4
      });
    } catch (error) {
      console.error('Error calculating CO2e:', error);
      setCalculationError('Failed to calculate CO2e emissions');
      setCo2eValue(null);
      setCo2eBreakdown(null);
    } finally {
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    calculateCO2e();
  }, [materialPercentages, distances, transportValues, gesteinComponents, selectedBindemittel, selectedProductionType, storageValues, fuelValues, constructionSiteTransport]);

  const handleTransportChange = (field) => (event) => {
    setTransportValues(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleBindemittelChange = (event) => {
    setSelectedBindemittel(event.target.value);
  };

  const handleProductionTypeChange = (event) => {
    setSelectedProductionType(event.target.value);
  };

  const handleStorageChange = (field) => (event) => {
    setStorageValues(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleFuelChange = (field) => (event) => {
    setFuelValues(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Handlers for gestein components
  const handleGesteinComponentChange = (index, field) => (event) => {
    setGesteinComponents(prev => prev.map((comp, i) =>
      i === index ? { ...comp, [field]: event.target.value } : comp
    ));
  };

  const handlePercentageChange = (field) => (event) => {
    setMaterialPercentages(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDistanceChange = (field) => (event) => {
    setDistances(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleConstructionSiteDistanceChange = (event) => {
    setConstructionSiteTransport(prev => ({
      ...prev,
      distance: event.target.value
    }));
  };

  const handleConstructionSiteVehicleChange = (event) => {
    setConstructionSiteTransport(prev => ({
      ...prev,
      vehicle: event.target.value
    }));
  };

  // Function to handle material specification data transfer
  const handleMaterialSpecificationTransfer = useCallback((values) => {
    // Update material percentages (fueller, asphalt, bindemittel)
    const materialPercentageFields = ['fueller', 'asphalt', 'bindemittel'];
    const materialPercentageValues = {};

    materialPercentageFields.forEach(field => {
      if (values[field] !== undefined) {
        materialPercentageValues[field] = values[field];
      }
    });

    if (Object.keys(materialPercentageValues).length > 0) {
      setMaterialPercentages(prev => ({
        ...prev,
        ...materialPercentageValues
      }));
    }

    // Update gestein components if provided
    if (values.gesteinComponents && values.gesteinComponents.length > 0) {
      setGesteinComponents(values.gesteinComponents.map(comp => ({
        nr: comp.nr || '',
        bezeichnung: comp.bezeichnung || 'Gesteinskörnungen',
        percentage: comp.percentage || '',
        distance: comp.distance || '',
        transport: 'Sattelzug Diesel'
      })));
    }

    // Update distance values
    const distanceValues = {};

    // Map fuellerDistance to fueller distance field
    if (values.fuellerDistance !== undefined) {
      distanceValues.fueller = values.fuellerDistance;
    }

    // Map asphaltDistance to asphalt distance field
    if (values.asphaltDistance !== undefined) {
      distanceValues.asphalt = values.asphaltDistance;
    }

    // Map bindemittelDistance (binder Hersteller → Lieferwerk) to bindemittel distance
    if (values.bindemittelDistance !== undefined) {
      distanceValues.bindemittel = values.bindemittelDistance;
    }

    if (Object.keys(distanceValues).length > 0) {
      setDistances(prev => ({
        ...prev,
        ...distanceValues
      }));
    }

    // Also populate construction site transport distance with asphalt distance
    if (values.asphaltDistance !== undefined) {
      setConstructionSiteTransport(prev => ({
        ...prev,
        distance: values.asphaltDistance
      }));
    }

    // Update selected bindemittel type if provided
    if (values.selectedBindemittel) {
      setSelectedBindemittel(values.selectedBindemittel);
    }

    console.log('Material specification transfer completed:', {
      percentages: materialPercentageValues,
      gesteinComponents: values.gesteinComponents,
      bindemittel: values.selectedBindemittel,
      constructionSiteDistance: values.asphaltDistance,
      transferredValues: values
    });
  }, []);

  // Expose the transfer function to parent component
  useEffect(() => {
    if (onMaterialSpecificationTransfer) {
      onMaterialSpecificationTransfer(handleMaterialSpecificationTransfer);
    }
  }, [onMaterialSpecificationTransfer, handleMaterialSpecificationTransfer]);

  const handleSave = async () => {
    const missing = validateRequiredFields();

    if (missing.length > 0) {
      setMissingFields(missing);
      setShowValidationPopup(true);
      return;
    }

    try {
      if (onSave && co2eValue !== null && materialName.trim()) {
        onSave(co2eValue, co2eBreakdown, materialName.trim());
        setDisplayName(materialName.trim());
      }
    } catch (error) {
      console.error('Error saving calculation:', error);
    }
  };
  const handlePermanentSave = async () => {
    const missing = validateRequiredFields();

    if (missing.length > 0) {
      setMissingFields(missing);
      setShowValidationPopup(true);
      return;
    }

    // Check if we have all required data
    if (!selectedMaterial?.name || !co2eValue || !projectId) {
      console.error('Missing required data for saving:', {
        materialName: selectedMaterial?.name,
        co2eValue,
        projectId
      });
      return;
    }

    // Check if this is a calculated layer material (no location data)
    const isCalculatedLayer = selectedMaterial.source === 'calculatedLayers';

    if (!isCalculatedLayer) {
      // For delivery note materials, check location data
      if (!selectedMaterial.productionPlant?.location) {
        console.error('Missing location data for delivery note material');
        return;
      }

      const { postalCode, city } = selectedMaterial.productionPlant.location;
      if (!postalCode || !city) {
        console.error('Missing location data:', { postalCode, city });
        return;
      }
    }

    try {
      // Prepare calculation inputs to save
      const calculationInputs = {
        materialPercentages,
        distances,
        transportValues,
        gesteinComponents,
        selectedBindemittel,
        selectedProductionType,
        storageValues,
        fuelValues,
        constructionSiteTransport
      };

      console.log('Saving calculation inputs with construction site transport:', calculationInputs.constructionSiteTransport);

      let result;

      if (isCalculatedLayer) {
        // Save to layer areas collection — resolve layerName to type key + entry index
        // layerName is e.g. "Deckschicht", "Deckschicht 1", "Binderschicht 2", "Tragschicht"
        const lName = selectedMaterial.layerName || '';
        let layerTypeKey, entryIndex;
        if (lName.startsWith('Deckschicht')) {
          layerTypeKey = 'deckschichten';
          entryIndex = parseInt(lName.replace('Deckschicht', '').trim(), 10) || 0;
          if (entryIndex > 0) entryIndex -= 1; // convert 1-based label to 0-based index
        } else if (lName.startsWith('Binderschicht')) {
          layerTypeKey = 'binderschichten';
          entryIndex = parseInt(lName.replace('Binderschicht', '').trim(), 10) || 0;
          if (entryIndex > 0) entryIndex -= 1;
        } else if (lName.startsWith('Tragschicht')) {
          layerTypeKey = 'tragschichten';
          entryIndex = parseInt(lName.replace('Tragschicht', '').trim(), 10) || 0;
          if (entryIndex > 0) entryIndex -= 1;
        } else {
          throw new Error(`Unknown layer type: ${lName}`);
        }

        result = await simulationService.saveLayerCO2(projectId, layerTypeKey, entryIndex, {
          co2Value: co2eValue,
          co2Breakdown: co2eBreakdown,
          calculationInputs
        });

        console.log('CO2 values saved to layer areas successfully', result);
      } else {
        // Save to delivery notes collection (original logic)
        const { postalCode, city } = selectedMaterial.productionPlant.location;

        result = await simulationService.updateCO2Values(projectId, {
          material: selectedMaterial.name,
          location: {
            postalCode,
            city
          },
          co2Value: co2eValue,
          breakdown: co2eBreakdown,
          calculationInputs
        });

        console.log('CO2 values saved to delivery notes successfully', result);
      }

      // Call the onPermanentSave callback if provided
      if (onPermanentSave && typeof onPermanentSave === 'function') {
        // Pass the material name, CO2 value, and breakdown to update the UI
        onPermanentSave(selectedMaterial.name, co2eValue, co2eBreakdown);
      }

      // Show success message to the user
      setHasSavedData(true);
    } catch (error) {
      console.error('Error saving CO2 values:', error);
      // Handle error in UI
    }
  };
  return (
    <div className="flex flex-col h-full justify-between">
      <div className="p-6 border-b flex-shrink-0">
        <h2 className="text-xl font-semibold mb-2">{displayName || 'Neues Material'}</h2>
        {isLoadingSaved ? (
          <div className="text-sm text-blue-500 mb-2">
            Lade gespeicherte Berechnungsdaten...
          </div>
        ) : hasSavedData ? (
          <div className="text-sm text-green-500 mb-2">
            Gespeicherte Berechnungsdaten geladen
          </div>
        ) : (
          <div className="text-sm text-gray-500 mb-2">
            Keine gespeicherten Daten für dieses Material
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="text-2xl font-semibold text-blue-500">
            {selectedMaterial?.value.toLocaleString('de-DE')} t
          </div>
          <div className="flex items-center gap-2 text-2xl font-semibold text-green-500">
            <Cloud className="w-6 h-6" />
            {isCalculating ? (
              <span className="text-gray-400">Calculating...</span>
            ) : calculationError ? (
              <span className="text-red-500">Error</span>
            ) : (
              `${co2eValue?.toFixed(2) || '0.00'} kg CO2e/t`
            )}
          </div>
        </div>
        {co2eBreakdown && !isCalculating && !calculationError && (
          <div className="mt-2 text-sm text-gray-600 space-y-1">
            <div>A1 - Rohstoffbereitstellung: {co2eBreakdown.A1.toFixed(2)} kg CO2e/t</div>
            <div>A2 - Transport: {co2eBreakdown.A2.toFixed(2)} kg CO2e/t</div>
            <div>A3 - Produktion: {co2eBreakdown.A3.toFixed(2)} kg CO2e/t</div>
            <div>A4 - Transport Baustelle: {co2eBreakdown.A4.toFixed(2)} kg CO2e/t</div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ height: 'calc(100% - 200px - 100px)' }}>
        {/* Füller <0,063 */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Füller {'<'}0,063</h3>
          <div className="grid grid-cols-3 gap-4">
            <input
              type="number"
              placeholder="M.-%"
              value={materialPercentages.fueller}
              onChange={handlePercentageChange('fueller')}
              className={getFieldStyling(materialPercentages.fueller, true)}
            />
            <input
              type="number"
              placeholder="km"
              value={distances.fueller}
              onChange={handleDistanceChange('fueller')}
              className={getFieldStyling(distances.fueller, true)}
            />
            <TransportSelect
              value={transportValues.fueller}
              onChange={handleTransportChange('fueller')}
              className={getSelectStyling(transportValues.fueller, false)}
            />
          </div>
        </div>

        {/* Übrigen Gesteinskörnungen - Individual Components */}
        {gesteinComponents.map((comp, index) => (
          <div key={index}>
            <h3 className="text-base font-medium text-gray-900 mb-4">
              {comp.nr ? `${comp.nr} ${comp.bezeichnung}` : comp.bezeichnung || 'Gesteinskörnungen'}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <input
                type="number"
                placeholder="M.-%"
                value={comp.percentage}
                onChange={handleGesteinComponentChange(index, 'percentage')}
                className={getFieldStyling(comp.percentage, true)}
              />
              <input
                type="number"
                placeholder="km"
                value={comp.distance}
                onChange={handleGesteinComponentChange(index, 'distance')}
                className={getFieldStyling(comp.distance, true)}
              />
              <TransportSelect
                value={comp.transport}
                onChange={handleGesteinComponentChange(index, 'transport')}
                className={getSelectStyling(comp.transport, false)}
              />
            </div>
          </div>
        ))}

        {/* Asphaltgranulat */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Asphaltgranulat</h3>
          <div className="grid grid-cols-3 gap-4">
            <input
              type="number"
              placeholder="M.-%"
              value={materialPercentages.asphalt}
              onChange={handlePercentageChange('asphalt')}
              className={getFieldStyling(materialPercentages.asphalt, true)}
            />
            <input
              type="number"
              placeholder="km"
              value={distances.asphalt}
              onChange={handleDistanceChange('asphalt')}
              className={getFieldStyling(distances.asphalt, true)}
            />
            <TransportSelect
              value={transportValues.asphalt}
              onChange={handleTransportChange('asphalt')}
              className={getSelectStyling(transportValues.asphalt, false)}
            />
          </div>
        </div>

        {/* Frischbindemittelzugabe */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Frischbindemittelzugabe</h3>
          <div className="grid grid-cols-3 gap-4">
            <input
              type="number"
              placeholder="M.-%"
              value={materialPercentages.bindemittel}
              onChange={handlePercentageChange('bindemittel')}
              className={getFieldStyling(materialPercentages.bindemittel, true)}
            />
            <input
              type="number"
              placeholder="km"
              value={distances.bindemittel}
              onChange={handleDistanceChange('bindemittel')}
              className={getFieldStyling(distances.bindemittel, true)}
            />
            <TransportSelect
              value={transportValues.bindemittel}
              onChange={handleTransportChange('bindemittel')}
              className={getSelectStyling(transportValues.bindemittel, false)}
            />
          </div>
        </div>

        {/* Frischbindemittel */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Frischbindemittel</h3>
          <div className="grid grid-cols-3 gap-4">
            <BindemittelSelect
              value={selectedBindemittel}
              onChange={handleBindemittelChange}
              className={getSelectStyling(selectedBindemittel, true)}
            />
          </div>
        </div>

        {/* Herstellungsart */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Herstellungsart</h3>
          <div className="grid grid-cols-3 gap-4">
            <ProductionTypeSelect
              value={selectedProductionType}
              onChange={handleProductionTypeChange}
              className={getSelectStyling(selectedProductionType, true)}
            />
          </div>
        </div>

        {/* Gesteinskörnungen */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Gesteinskörnungen</h3>
          <div className="grid grid-cols-2 gap-4">
            <StorageSelect
              value={storageValues.gestein}
              onChange={handleStorageChange('gestein')}
              className={getSelectStyling(storageValues.gestein, true)}
            />
            <FuelSelect
              value={fuelValues.gestein}
              onChange={handleFuelChange('gestein')}
              className={getSelectStyling(fuelValues.gestein, true)}
            />
          </div>
        </div>

        {/* Asphaltgranulat - Second Section */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Asphaltgranulat</h3>
          <div className="grid grid-cols-2 gap-4">
            <StorageSelect
              value={storageValues.asphalt}
              onChange={handleStorageChange('asphalt')}
              className={getSelectStyling(storageValues.asphalt, true)}
            />
            <FuelSelect
              value={fuelValues.asphalt}
              onChange={handleFuelChange('asphalt')}
              className={getSelectStyling(fuelValues.asphalt, true)}
            />
          </div>
        </div>

        {/* Transport Baustelle */}
        <div>
          <h3 className="text-base font-medium text-gray-900 mb-4">Transport Baustelle</h3>
          <div className="grid grid-cols-2 gap-4">
            <input
              type="number"
              placeholder="km"
              value={constructionSiteTransport.distance}
              onChange={handleConstructionSiteDistanceChange}
              className={getFieldStyling(constructionSiteTransport.distance, true)}
            />
            <TransportSelect
              value={constructionSiteTransport.vehicle}
              onChange={handleConstructionSiteVehicleChange}
              className={getSelectStyling(constructionSiteTransport.vehicle, true)}
            />
          </div>
        </div>
      </div>


      <div className="p-6 border-t mt-auto space-y-4" style={{ marginTop: 'auto' }}>
        <input
          type="text"
          value={materialName}
          onChange={(e) => setMaterialName(e.target.value)}
          placeholder="Material Name"
          className={getFieldStyling(materialName, true)}
        />
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleSave}
            disabled={isCalculating || !co2eValue || !materialName.trim()}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-xl transition-colors"
          >
            Anzeigen
          </button>
          <button
  onClick={handlePermanentSave}
  disabled={isCalculating || !co2eValue || !projectId || !selectedMaterial?.name ||
    (selectedMaterial?.source !== 'calculatedLayers' &&
     (!selectedMaterial?.productionPlant?.location?.postalCode ||
      !selectedMaterial?.productionPlant?.location?.city))}
  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-xl transition-colors"
>
  Speichern
</button>
        </div>
      </div>

      {/* Validation Popup */}
      {showValidationPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Fehlende Eingaben</h3>
            </div>

            <p className="text-gray-600 mb-6">
              Bitte füllen Sie die orange markierten Felder aus.
            </p>

            <div className="flex justify-end">
              <button
                onClick={() => setShowValidationPopup(false)}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculator;